import { BaseApiClient } from '@/api/baseApiClient';
import { getConfig } from '@/config/runtimeConfig';
import type {
  BrowseAutocompleteSuggestion,
  BrowseRequest,
  BrowseResponse,
  CountRequest,
  CountResponse,
  SearchFacets,
  SearchRequest,
  SearchResponse,
  WireBrowseResponse,
  WireSearchFacet,
  WireSearchResponse,
} from '@/types/searchResponse.types';

/**
 * Normalize the live gw-search-api facet wire shape into the app-facing
 * `SearchFacets` object that gw-ui's SearchFacetRail consumes.
 *
 * The provider (GW-7127 deriveFacets) returns an ARRAY of
 * `{ field, buckets: [{ value, count }] }`, but SearchFacetRail expects an
 * OBJECT keyed by field: `facets.itemType = [{ key, count }]`. Without this
 * translation `facets?.itemType` is always undefined and every facet-rail
 * category row renders a count of 0 even when results exist.
 *
 * Accepts BOTH shapes defensively: an already-keyed `SearchFacets` object (the
 * shape the Pact fixtures historically used, and what tests mocking
 * `searchApi.search`'s return value pass) is returned unchanged.
 */
export function normalizeFacets(
  facets: WireSearchFacet[] | SearchFacets | undefined
): SearchFacets | undefined {
  if (facets === undefined) {
    return undefined;
  }
  // Already the keyed object shape — pass through unchanged.
  if (!Array.isArray(facets)) {
    return facets;
  }
  const FIELD_RENAME: Partial<Record<string, keyof SearchFacets>> = {
    new: 'isNew',
    publisherOrgId: 'publisher',
  };
  const normalized: SearchFacets = {};
  for (const { field, buckets } of facets) {
    const targetKey = (FIELD_RENAME[field] ?? field) as keyof SearchFacets;
    normalized[targetKey] = buckets.map((b) => ({
      key: b.value,
      count: b.count,
    }));
  }
  return normalized;
}

/**
 * Normalize a raw wire response into the app-facing `SearchResponse`, folding
 * the array-shaped `facets` into the keyed `SearchFacets` object.
 */
function normalizeSearchResponse(wire: WireSearchResponse): SearchResponse {
  return { ...wire, facets: normalizeFacets(wire.facets) };
}

/**
 * Search API client for gw-search-api endpoints
 * Supports both public and authenticated search
 */
export class SearchApiClient extends BaseApiClient {
  constructor(requiresAuth: boolean = false) {
    super({
      baseURLFactory: () => getConfig().searchApiBaseUrl ?? getConfig().apiBaseUrl,
      requiresAuth,
    });
  }

  /**
   * Public search endpoint
   */
  async search(
    request: SearchRequest,
    options?: { signal?: AbortSignal }
  ): Promise<SearchResponse> {
    // GW-13059 / GW-13056: pass consumer page size (default 20) to the provider.
    const payload = { ...request, size: request.size ?? 20 };
    const wire = await this.post<WireSearchResponse>('/v1/search', payload, options);
    return normalizeSearchResponse(wire);
  }

  /**
   * Authenticated search endpoint (returns personalized/restricted results)
   */
  async searchAuthenticated(request: SearchRequest): Promise<SearchResponse> {
    const wire = await this.post<WireSearchResponse>('/v1/search/authenticated', request);
    return normalizeSearchResponse(wire);
  }

  /**
   * Category browse endpoint (POST /v1/browse, GW-12074).
   *
   * Powers the four marketplace category pages. Accepts an empty `q` (lists all
   * items of `type`) and REQUIRES a non-empty `type` array. Maps client
   * normalizes the array-shaped wire facets into the keyed `SearchFacets` object
   * SearchFacetRail consumes.
   */
  async browse(
    request: BrowseRequest,
    options?: { signal?: AbortSignal }
  ): Promise<BrowseResponse> {
    const wire = await this.post<WireBrowseResponse>('/v1/browse', request, options);
    return { ...wire, facets: normalizeFacets(wire.facets) };
  }

  /**
   * Per-type catalog count endpoint (POST /v1/count, PRP-MKTPL-020).
   *
   * Returns a keyed `counts` object (one key per requested `type`) plus a
   * `total` summed across types, consistent with the browse/search eligible set
   * for the SAME caller (shared `buildBrowseQuery` + `buildCatalogVisibilityFilter`).
   * REQUIRES a non-empty `type` array; accepts an empty/absent `q` (→ match_all).
   * On the auth-capable client instance the Authorization header is attached
   * automatically when the user is logged in, so an authenticated caller's counts
   * widen to include the org's private/shared items.
   */
  async count(request: CountRequest, options?: { signal?: AbortSignal }): Promise<CountResponse> {
    return this.post<CountResponse>('/v1/count', request, options);
  }

  /**
   * Rich browse autocomplete (GET /v1/browse/autocomplete, GW-12074).
   *
   * Unlike `autocomplete()` (bare string array) this returns objects carrying
   * `{ name, itemType, slug }` so the dropdown can link straight to a detail
   * page. `type` is REQUIRED (category scope); `limit` defaults to 10 (max 20).
   */
  async browseAutocomplete(
    query: string,
    type: string,
    limit: number = 10
  ): Promise<BrowseAutocompleteSuggestion[]> {
    const params = new URLSearchParams({ q: query, type });
    params.set('limit', String(limit));
    const res = await this.get<unknown>(`/v1/browse/autocomplete?${params.toString()}`);

    // Normalize: bare array (real endpoint) or object with suggestions (defensive)
    const arr = Array.isArray(res)
      ? res
      : res && typeof res === 'object' && 'suggestions' in res
        ? ((res as { suggestions?: unknown[] }).suggestions ?? [])
        : [];
    return (arr as BrowseAutocompleteSuggestion[]).filter(
      (s): s is BrowseAutocompleteSuggestion =>
        !!s && typeof s === 'object' && typeof (s as { name?: unknown }).name === 'string'
    );
  }

  /**
   * Autocomplete suggestions
   * Returns a bare string array (matches live endpoint + pact contract)
   * @param type Optional category scope (mission|dataset|service|application, GW-8575)
   */
  async autocomplete(query: string, type?: string): Promise<string[]> {
    const params = new URLSearchParams({ q: query });
    if (type) {
      params.set('type', type);
    }
    const res = await this.get<unknown>(`/v1/search/autocomplete?${params.toString()}`);

    // Normalize: bare array (real endpoint) or object with suggestions (defensive)
    if (Array.isArray(res)) {
      return res as string[];
    }
    if (res && typeof res === 'object' && 'suggestions' in res) {
      return (res as { suggestions?: string[] }).suggestions ?? [];
    }
    return [];
  }
}

// Export singleton instance for public (anonymous) catalog search/browse/count.
// requiresAuth:false — never attaches an Authorization header. Preserved unchanged
// for existing anonymous callers (PRP-MKTPL-020 Task 10).
export const searchApi = new SearchApiClient(false);

// Auth-capable singleton for the DUAL-MODE gw-search-api routes (/v1/search,
// /v1/browse, /v1/count). requiresAuth:true means BaseApiClient attaches the
// Authorization header ONLY when a token is present (getHeaders: `if (token)`),
// and omits it otherwise — so this instance sends auth when the user is logged in
// and falls through anonymously when not. The dual-mode routes never 401 for an
// anonymous caller, so this is safe to use for logged-in catalog reads
// (PRP-MKTPL-020 Task 10).
export const searchApiAuthed = new SearchApiClient(true);

/**
 * Pick the correct catalog client for the caller's auth state: the auth-capable
 * instance when logged in (forwards JWT claims so browse/search/count widen to
 * the org's private/shared items), the anonymous singleton otherwise. This is the
 * single place the SPA's catalog surfaces (composed Search page, category tiles,
 * empty-q browse, dedicated browse pages) decide auth-awareness (PRP-MKTPL-020).
 */
export function getSearchClient(isAuthenticated: boolean): SearchApiClient {
  return isAuthenticated ? searchApiAuthed : searchApi;
}
